import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import axios from "../axiosConfig";
import Modal from "./Modal";
import ParticipantList from "./ParticipantList";
import Table from "./Table";
import { parseDbDate } from "../utils/dates";
import {
  getLocalDatetimeInputValue,
  toUTCISOString,
  toLocalISOString,
  useFieldOptions,
  fieldOptionList,
} from "./Utils";
import Checkbox from './Checkbox';
import ContentPresets from './ContentPresets';
import DeadlineField from './DeadlineField';
import NewTaskModal, { taskCopyValues } from './NewTaskModal';

const TasksTable = forwardRef(
  (
    {
      contactId,
      accountId,
      jobId,
      defaultParticipants = [],
      onInteractionCreated,
      fillPage = false,
    },
    ref,
  ) => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const fieldOptions = useFieldOptions();

    // The new-task form: { initialValues } while open (null = blank task).
    const [newTask, setNewTask] = useState(null);
    const [editingTask, setEditingTask] = useState(null);

    const [formData, setFormData] = useState({
      content: "",
      format: "",
      type: "",
      participants: defaultParticipants,
      hasDeadline: true,
      deadlineLocal: getLocalDatetimeInputValue(),
      isCompleted: false,
      completedLocal: getLocalDatetimeInputValue(),
    });

    const fetchTasks = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (contactId) params.append("contact_id", contactId);
        if (accountId) params.append("account_id", accountId);
        if (jobId) params.append("job_id", jobId);

        const res = await axios.get(`/tasks/?${params.toString()}`, {
          withCredentials: true,
        });

        // Process tasks to split participants into separate arrays
        const processedTasks = (res.data || []).map((task) => ({
          ...task,
          accountParticipants:
            task.participants?.filter((p) => p.type === "account") || [],
          contactParticipants:
            task.participants?.filter((p) => p.type === "contact") || [],
        }));

        setTasks(processedTasks);
      } catch (err) {
        console.error("Error fetching tasks:", err);
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      fetchTasks();
    }, [contactId, accountId, jobId]);

    // Expose fetchTasks to parent component
    useImperativeHandle(
      ref,
      () => ({
        fetchTasks,
      }),
      [],
    );

    const openEditModal = (task) => {
      setEditingTask(task);
      setFormData({
        content: task.content,
        format: task.format,
        type: task.type,
        participants: task.participants || [],
        hasDeadline: !!task.deadline,
        deadlineLocal: toLocalISOString(task.deadline),
        isCompleted: !!task.completed_at,
        completedLocal: task.completed_at
          ? toLocalISOString(task.completed_at)
          : getLocalDatetimeInputValue(),
      });
    };

    const handleFormChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    };

    const handleParticipantsChange = (participants) => {
      setFormData((prev) => ({ ...prev, participants }));
    };

    const buildPayload = () => ({
      content: formData.content,
      format: formData.format,
      type: formData.type,
      participants: formData.participants.map((p) => ({
        id: p.id,
        type: p.type,
        is_sender: p.is_sender || false,
      })),
      deadline: formData.hasDeadline
        ? toUTCISOString(formData.deadlineLocal)
        : null,
      job_id: jobId || null,
      completed_at: formData.isCompleted
        ? toUTCISOString(formData.completedLocal)
        : null,
    });

    const handleEditSubmit = async () => {
      if (!editingTask) return;
      try {
        const wasIncomplete = !editingTask.completed_at;
        const willBeComplete = formData.isCompleted;

        await axios.patch(`/tasks/${editingTask.id}/`, buildPayload(), {
          withCredentials: true,
        });
        setEditingTask(null);
        fetchTasks();

        // Notify parent that a task was completed (converted to interaction)
        if (wasIncomplete && willBeComplete && onInteractionCreated) {
          onInteractionCreated();
        }
      } catch (err) {
        console.error("Failed to update task:", err);
      }
    };

    const handleDelete = async () => {
      if (!editingTask) return;
      try {
        await axios.delete(`/tasks/${editingTask.id}/`, {
          withCredentials: true,
        });
        setEditingTask(null);
        fetchTasks();
      } catch (err) {
        console.error("Failed to delete task:", err);
      }
    };

    const handleQuickComplete = async (task) => {
      try {
        await axios.patch(
          `/tasks/${task.id}/`,
          { completed_at: new Date().toISOString() },
          { withCredentials: true },
        );
        fetchTasks();
        if (onInteractionCreated) onInteractionCreated();
      } catch (err) {
        console.error("Failed to complete task:", err);
      }
    };

    const handleQuickDelete = async (task) => {
      if (!window.confirm("Are you sure you want to delete this task?")) return;
      try {
        await axios.delete(`/tasks/${task.id}/`, { withCredentials: true });
        fetchTasks();
      } catch (err) {
        console.error("Failed to delete task:", err);
      }
    };

    const handlePresetContent = (content) => {
      setFormData((prev) => ({ ...prev, content }));
    };

    // Define table columns
    const columns = [
      {
        key: "deadline",
        header: "Deadline",
        type: "date",
        width: 80,
      },
      {
        key: "content",
        header: "Content",
        type: "text",
        truncate: 200,
        width: 256,
      },
      {
        key: "accountParticipants",
        header: "Accounts",
        type: "participant",
        sortable: false,
        ftlInitials: true,
        width: 128,
      },
      {
        key: "contactParticipants",
        header: "Contacts",
        type: "participant",
        sortable: false,
        ftlInitials: true,
        width: 128,
      },
      {
        key: "type",
        header: "Type",
        type: "discrete",
        width: 80,
      },
      {
        key: "format",
        header: "Format",
        type: "discrete",
        width: 80,
      },
      {
        key: "actions",
        header: "Actions",
        type: "static",
        sortable: false,
        width: 160,
        render: (value, row) => (
          <div onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() =>
                setNewTask({ initialValues: taskCopyValues(row) })
              }
              title="New task with the same details"
            >
              Copy
            </button>
            <button
              type="button"
              onClick={() => handleQuickComplete(row)}
              title="Mark as completed now"
            >
              Complete
            </button>
            <button
              type="button"
              onClick={() => handleQuickDelete(row)}
              className="delete-btn"
            >
              Delete
            </button>
          </div>
        ),
      },
    ];

    return (
      <div
        className="interactions-container"
        style={
          fillPage
            ? {
                height: "100%",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }
            : undefined
        }
      >
        <div className="interactions-header">
          <button onClick={() => setNewTask({ initialValues: null })}>
            + New Task
          </button>
        </div>

        <NewTaskModal
          isOpen={!!newTask}
          onClose={() => setNewTask(null)}
          initialValues={newTask?.initialValues || null}
          defaultParticipants={defaultParticipants}
          jobId={jobId}
          onCreated={fetchTasks}
        />

        {/* Edit Task Modal */}
        <Modal
          isOpen={!!editingTask}
          onClose={() => setEditingTask(null)}
          title="Edit Task"
        >
          {editingTask && (
            <>
              {(() => {
                const loggerName = [
                  editingTask.logger?.first_name,
                  editingTask.logger?.last_name,
                ]
                  .filter(Boolean)
                  .join(" ");
                const createdAt = parseDbDate(
                  editingTask.created_at,
                )?.toLocaleString("en-GB");
                if (!loggerName && !createdAt) return null;
                return (
                  <div
                    style={{
                      fontStyle: "italic",
                      marginBottom: "15px",
                      color: "#666",
                    }}
                  >
                    Created
                    {loggerName ? ` by ${loggerName}` : ""}
                    {createdAt ? ` on ${createdAt}` : ""}
                  </div>
                );
              })()}
              <form
                className="interaction-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleEditSubmit();
                }}
              >
                <label>
                  Content:
                  <textarea
                    autoComplete="off"
                    name="content"
                    value={formData.content}
                    onChange={handleFormChange}
                    rows={4}
                    required
                  />
                </label>
                <ContentPresets onSelect={handlePresetContent} />

                <label>
                  Format:
                  <select
                    autoComplete="off"
                    name="format"
                    value={formData.format}
                    onChange={handleFormChange}
                    required
                  >
                    {fieldOptionList(fieldOptions, "interactions", "format").map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>

                <label>
                  Type:
                  <select
                    autoComplete="off"
                    name="type"
                    value={formData.type}
                    onChange={handleFormChange}
                    required
                  >
                    {fieldOptionList(fieldOptions, "interactions", "type").map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>

                <ParticipantList
                  participants={formData.participants}
                  onChange={handleParticipantsChange}
                />

                <div className="checkbox-group">
                  <Checkbox
                    name="hasDeadline"
                    checked={formData.hasDeadline}
                    onChange={handleFormChange}
                    label={
                      formData.hasDeadline ? "Deadline Date/time:" : "Set Deadline"
                    }
                  />
                </div>

                {formData.hasDeadline && (
                  <DeadlineField
                    name="deadlineLocal"
                    value={formData.deadlineLocal}
                    onChange={handleFormChange}
                  />
                )}

                <div className="checkbox-group">
                  <Checkbox
                    name="isCompleted"
                    checked={formData.isCompleted}
                    onChange={handleFormChange}
                    label={
                      formData.isCompleted ? "Time Completed:" : "Complete Task"
                    }
                  />
                </div>

                {formData.isCompleted && (
                  <input
                    autoComplete="off"
                    type="datetime-local"
                    name="completedLocal"
                    value={formData.completedLocal}
                    onChange={handleFormChange}
                    required
                  />
                )}

                <div className="modal-footer">
                  <button type="submit" className="submit-btn">
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="delete-btn"
                  >
                    Delete Task
                  </button>
                  <button type="button" onClick={() => setEditingTask(null)}>
                    Cancel
                  </button>
                </div>
              </form>
            </>
          )}
        </Modal>

        {/* Task Table */}
        <Table
          data={tasks}
          columns={columns}
          loading={loading}
          emptyMessage="No tasks found."
          onRowClick={(task) => openEditModal(task)}
          defaultSortColumn="deadline"
          defaultSortDirection="asc"
          fillPage={fillPage}
        />
      </div>
    );
  },
);

export default TasksTable;
